import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, User, LogOut, LayoutDashboard, ShieldCheck, Bell, Moon, Sun, Newspaper, Home, Grid, FolderHeart, HelpCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useNotifications } from '@/context/NotificationContext';
import SiteLogo from './SiteLogo';
import LoginModal from './LoginModal';
import GlobalSearchModal from './GlobalSearchModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, logout, isAdmin, profile, openAuthModal } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const location = useLocation();

  const navLinks = [
    { name: 'Beranda', path: '/' },
    { name: 'Layanan', path: '/services' },
    { name: 'Produk', path: '/products' },
    { name: 'Edukasi', path: '/education' },
    { name: 'Artikel', path: '/articles' },
    { name: 'Regulasi', path: '/regulations' },
    { name: 'Pusat Bantuan', path: '/help' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300">
        <div className="bg-background/95 backdrop-blur-md border-b border-gold/10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 md:h-20 items-center">
              <Link to="/" className="flex items-center space-x-2">
                <SiteLogo className="w-7 h-7 md:w-8 md:h-8 rounded-lg shadow-sm" />
                <span className="text-xl md:text-2xl font-black text-primary tracking-tighter">Azman204</span>
              </Link>

              {/* Desktop Nav - Only visible on MD+ */}
              <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`text-sm font-bold transition-colors hover:text-primary whitespace-nowrap ${
                      location.pathname === link.path ? 'text-primary' : 'text-foreground/80'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}

                <div className="flex items-center space-x-2 xl:space-x-4 border-l border-foreground/10 pl-4 xl:pl-6 h-8">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-foreground/60 hover:text-primary h-9 w-9 rounded-full"
                    onClick={() => setIsSearchOpen(true)}
                    title="Pencarian Global"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-foreground/60 hover:text-primary h-9 w-9 rounded-full"
                    onClick={toggleTheme}
                  >
                    {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      nativeButton={true}
                      render={
                        <Button variant="ghost" size="icon" className="text-foreground/60 hover:text-primary h-9 w-9 rounded-full relative">
                          <Bell className="h-5 w-5" />
                          {unreadCount > 0 && (
                            <span className="absolute top-2 right-2 h-2 w-2 bg-red-500 rounded-full border border-background shrink-0" />
                          )}
                        </Button>
                      }
                    />
                    <DropdownMenuContent align="end" className="w-80 bg-popover border-gold/20 text-foreground p-0">
                      <DropdownMenuGroup>
                        <DropdownMenuLabel className="p-4 border-b border-gold/10">Notifikasi</DropdownMenuLabel>
                      </DropdownMenuGroup>
                      <div className="max-h-80 overflow-y-auto font-sans text-foreground">
                        {notifications.length === 0 ? (
                          <div className="p-8 text-center text-foreground/40 italic text-sm">
                            Tidak ada notifikasi
                          </div>
                        ) : (
                          notifications.map(n => (
                            <div 
                              key={n.id} 
                              onClick={() => markAsRead(n.id)}
                              className={`p-4 border-b border-gold/10 hover:bg-muted transition-colors cursor-pointer ${!n.read ? 'bg-gold/5' : ''}`}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <p className="text-sm font-bold text-primary">{n.title}</p>
                                {!n.read && <div className="w-2 h-2 bg-primary rounded-full" />}
                              </div>
                              <p className="text-xs text-foreground/60">{n.message}</p>
                              <p className="text-[10px] text-foreground/30 mt-2">{n.time}</p>
                            </div>
                          ))
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <DropdownMenuItem 
                          onClick={markAllAsRead}
                          className="p-3 text-center text-xs text-primary hover:bg-gold/10 cursor-pointer justify-center font-bold"
                        >
                          Tandai semua telah dibaca
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {user ? (
                    <div className="flex items-center space-x-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          nativeButton={true}
                          render={
                            <button type="button" className="inline-flex items-center justify-center hover:bg-muted aria-expanded:bg-muted relative h-10 w-10 rounded-full border border-gold/30 p-0 overflow-hidden" />
                          }
                        >
                          {profile?.photoURL || user?.photoURL ? (
                            <img src={profile?.photoURL || user?.photoURL} alt={user.displayName || ''} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            <User className="h-5 w-5 text-primary" />
                          )}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-popover border-gold/20 text-foreground">
                          <DropdownMenuGroup>
                            <DropdownMenuLabel>Akun Saya</DropdownMenuLabel>
                          </DropdownMenuGroup>
                          <DropdownMenuSeparator className="bg-gold/10" />
                          {(isAdmin || user?.email === 'azmankoe916@gmail.com') && (
                            <DropdownMenuItem
                              nativeButton={false}
                              render={
                                <Link to="/admin" className="cursor-pointer text-primary font-bold" />
                              }
                            >
                              <ShieldCheck className="mr-2 h-4 w-4" />
                              <span>Admin Panel</span>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            nativeButton={false}
                            render={
                              <Link to="/dashboard" className="cursor-pointer" />
                            }
                          >
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            <span>Dashboard</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      <Button 
                        variant="ghost" 
                        onClick={logout}
                        className="text-foreground/60 hover:text-red-400 hover:bg-muted text-xs font-bold"
                      >
                        <LogOut className="h-4 w-4 mr-1" /> Keluar
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <Button 
                        variant="ghost"
                        onClick={() => openAuthModal('login')}
                        className="text-foreground hover:text-primary font-semibold"
                      >
                        Masuk
                      </Button>
                      <Button 
                        onClick={() => openAuthModal('register')}
                        className="bg-gold hover:bg-gold-light text-navy font-bold rounded-full px-6 shadow-lg shadow-gold/10"
                      >
                        Daftar Gratis
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile UI - Icons only, minimal header */}
              <div className="lg:hidden flex items-center space-x-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-foreground/60 hover:text-primary h-8 w-8 rounded-full"
                  onClick={() => setIsSearchOpen(true)}
                  title="Pencarian Global"
                >
                  <Search className="h-4 w-4" />
                </Button>

                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-foreground/60 hover:text-primary h-8 w-8 rounded-full"
                  onClick={toggleTheme}
                >
                  {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger
                    nativeButton={true}
                    render={
                      <Button variant="ghost" size="icon" className="text-foreground/60 hover:text-primary h-8 w-8 rounded-full relative">
                        <Bell className="h-4 w-4" />
                        {unreadCount > 0 && (
                          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 bg-red-500 rounded-full border border-background shrink-0" />
                        )}
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="end" className="w-72 bg-popover border-gold/20 text-foreground p-0">
                    <DropdownMenuGroup>
                      <DropdownMenuLabel className="p-3 border-b border-gold/10 text-xs font-sans">Notifikasi</DropdownMenuLabel>
                    </DropdownMenuGroup>
                    <div className="max-h-64 overflow-y-auto font-sans">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center text-foreground/40 italic text-xs">
                          Tidak ada notifikasi
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div 
                            key={n.id} 
                            onClick={() => markAsRead(n.id)}
                            className={`p-3 border-b border-gold/10 hover:bg-muted transition-colors cursor-pointer ${!n.read ? 'bg-gold/5' : ''}`}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <p className="text-xs font-bold text-primary">{n.title}</p>
                              {!n.read && <div className="w-1.5 h-1.5 bg-primary rounded-full" />}
                            </div>
                            <p className="text-[10px] text-foreground/60 font-sans">{n.message}</p>
                            <p className="text-[9px] text-foreground/30 mt-2 font-sans">{n.time}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {user ? (
                  <div className="flex items-center space-x-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        nativeButton={true}
                        render={
                          <button type="button" className="inline-flex items-center justify-center hover:bg-muted aria-expanded:bg-muted relative h-8 w-8 rounded-full border border-gold/30 p-0 overflow-hidden" />
                        }
                      >
                        {profile?.photoURL || user?.photoURL ? (
                          <img src={profile?.photoURL || user?.photoURL} alt={user.displayName || ''} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <User className="h-4 w-4 text-primary" />
                        )}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-popover border-gold/20 text-foreground">
                        <DropdownMenuGroup>
                          <DropdownMenuLabel>Akun Saya</DropdownMenuLabel>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator className="bg-gold/10" />
                        {(isAdmin || user?.email === 'azmankoe916@gmail.com') && (
                          <DropdownMenuItem
                            nativeButton={false}
                            render={
                              <Link to="/admin" className="cursor-pointer text-primary font-bold" />
                            }
                          >
                            <ShieldCheck className="mr-2 h-4 w-4" />
                            <span>Admin Panel</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          nativeButton={false}
                          render={
                            <Link to="/dashboard" className="cursor-pointer" />
                          }
                        >
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          <span>Dashboard</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-gold/10" />
                        <DropdownMenuItem
                          nativeButton={true}
                          render={
                            <button onClick={logout} className="w-full text-left text-red-400 font-bold" />
                          }
                        >
                          <LogOut className="mr-2 h-4 w-4" /> Keluar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) : (
                  <Button 
                    variant="ghost"
                    onClick={() => openAuthModal('login')}
                    className="text-foreground hover:text-primary font-bold px-2 text-xs"
                  >
                    Masuk
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <LoginModal />
      <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}
